/**
 * Browser-durable storage for the document source currently open in each
 * authenticated Studio project.
 *
 * ─── The defect this closes ──────────────────────────────────────────────
 * The DOCX viewer's parsed document lived only in a module-scoped session
 * (`docx-source-viewer-state.ts`). That already survives a rail switch, but a
 * page reload emptied it, and the operator's only recovery was to find the file
 * on disk and upload it again. For the audited 1.6 MB WonBanking guide that is
 * a 739 ms parse thrown away by pressing F5.
 *
 * ─── Why the ORIGINAL BYTES, not the extraction ──────────────────────────
 * We store what the operator gave us and re-run the same extractor on restore.
 *   • It is the same "keep the original first" rule the .fig import follows.
 *   • The restore path exercises the production parse, so a restored document
 *     cannot silently diverge from a freshly-opened one.
 *   • The extraction of a real guide (3,282 paragraphs) serializes larger than
 *     the 1.6 MB source it came from, so bytes are also the smaller record.
 *
 * ─── Why IndexedDB, not localStorage ─────────────────────────────────────
 * `localStorage` is a ~5 MB string store, and this project has already MEASURED
 * it rejecting a real source: serializing the audited `.fig` workspace throws
 * `QuotaExceededError` (see the module header of
 * `openpencil-source-workspace-storage.ts`). A 1.6 MB binary would have to be
 * base64'd to ~2.2 MB of string to even attempt it. IndexedDB stores the
 * `ArrayBuffer` natively and is already used in this app
 * (`store/project-slice.ts`, imported Coogo application snapshots), so this
 * adds no dependency and no new storage concept.
 *
 * ─── Why every function returns a STATUS ─────────────────────────────────
 * Quota, private-browsing modes and blocked-storage settings all make a write
 * fail. The prevailing `catch {}` pattern would let the interface keep claiming
 * "this survives a reload" while nothing was written — the exact lie this lane
 * exists to remove. Callers get `"rejected"` with a non-technical reason and
 * say so on screen.
 */

const DB_NAME = "cogo-studio-document-source";
const DB_VERSION = 1;
const STORE = "sources";

const RECORD_KEY_PREFIX = "scope:";

export interface DocumentSourceScope {
  principalId: string;
  projectId: string;
}

export interface StoredDocumentSource {
  scope: DocumentSourceScope;
  fileName: string;
  bytes: ArrayBuffer;
  savedAt: number;
  /** SHA-256 of `bytes`, lowercase 64-char hex. */
  sha256: string;
  sizeBytes: number;
}

/** Why a write did not stick, in terms a creator-facing sentence can use. */
export type DocumentSourceRejectionReason =
  /** No IndexedDB in this context (private mode, blocked storage, SSR, jsdom). */
  | "unavailable"
  /** The browser refused the size. */
  | "quota"
  /** Reached the store but the transaction did not complete. */
  | "failed";

export type DocumentSourceSaveResult =
  | { state: "stored"; savedAt: number; sha256: string; sizeBytes: number }
  | { state: "rejected"; reason: DocumentSourceRejectionReason };

function normalizeScope(
  scope: DocumentSourceScope,
): DocumentSourceScope | null {
  const principalId = scope.principalId.trim();
  const projectId = scope.projectId.trim();
  return principalId && projectId ? { principalId, projectId } : null;
}

export function documentSourceScopeKey(
  scope: DocumentSourceScope,
): string | null {
  const normalized = normalizeScope(scope);
  if (!normalized) return null;
  return `${RECORD_KEY_PREFIX}${encodeURIComponent(normalized.principalId)}:${encodeURIComponent(
    normalized.projectId,
  )}`;
}

function hasIndexedDb(): boolean {
  return typeof indexedDB !== "undefined" && indexedDB !== null;
}

function isQuotaError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const name = "name" in error ? String((error as { name: unknown }).name) : "";
  return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED";
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("document source store unavailable"));
    // Firefox private mode resolves neither callback; a blocked open is a
    // refusal, not a hang.
    request.onblocked = () =>
      reject(request.error ?? new Error("document source store blocked"));
  });
}

/**
 * Copy `bytes` into a standalone `ArrayBuffer`.
 *
 * `Uint8Array.buffer` can be a VIEW over a larger buffer (that is exactly what
 * `new Uint8Array(await file.arrayBuffer())` avoids but a caller-supplied
 * subarray would not), and structured-cloning the underlying buffer would then
 * persist bytes outside the view. Slicing pins the record to what the view
 * actually covers.
 */
function toStandaloneBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

/** Document 24 §12.3 descriptor: SHA-256 of the source bytes, lowercase hex. */
async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    toStandaloneBuffer(bytes),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

export async function saveDocumentSource(
  scope: DocumentSourceScope,
  fileName: string,
  bytes: Uint8Array,
): Promise<DocumentSourceSaveResult> {
  const normalizedScope = normalizeScope(scope);
  const recordKey = normalizedScope
    ? documentSourceScopeKey(normalizedScope)
    : null;
  if (!hasIndexedDb() || !normalizedScope || !recordKey) {
    return { state: "rejected", reason: "unavailable" };
  }
  const savedAt = Date.now();
  let db: IDBDatabase | null = null;
  try {
    const sha256 = await sha256Hex(bytes);
    const sizeBytes = bytes.byteLength;
    const record: StoredDocumentSource = {
      scope: normalizedScope,
      fileName,
      bytes: toStandaloneBuffer(bytes),
      savedAt,
      sha256,
      sizeBytes,
    };
    db = await openDb();
    const database = db;
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, "readwrite");
      transaction.objectStore(STORE).put(record, recordKey);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () =>
        reject(transaction.error ?? new Error("document source write failed"));
      transaction.onabort = () =>
        reject(transaction.error ?? new Error("document source write aborted"));
    });
    return { state: "stored", savedAt, sha256, sizeBytes };
  } catch (error) {
    // Developer signal kept; creator signal is the returned status, not this.
    console.warn("[document-source] not stored in this browser", error);
    return {
      state: "rejected",
      reason: isQuotaError(error) ? "quota" : "failed",
    };
  } finally {
    db?.close();
  }
}

/** `null` for "nothing stored" AND for "cannot read" — both mean no restore. */
export async function readDocumentSource(
  scope: DocumentSourceScope,
): Promise<StoredDocumentSource | null> {
  const normalizedScope = normalizeScope(scope);
  const recordKey = normalizedScope
    ? documentSourceScopeKey(normalizedScope)
    : null;
  if (!hasIndexedDb() || !normalizedScope || !recordKey) return null;
  let db: IDBDatabase | null = null;
  try {
    db = await openDb();
    const database = db;
    const record = await new Promise<StoredDocumentSource | null>(
      (resolve, reject) => {
        const transaction = database.transaction(STORE, "readonly");
        const request = transaction.objectStore(STORE).get(recordKey);
        request.onsuccess = () =>
          resolve((request.result as StoredDocumentSource | undefined) ?? null);
        request.onerror = () =>
          reject(request.error ?? new Error("document source read failed"));
        transaction.onerror = () =>
          reject(transaction.error ?? new Error("document source read failed"));
      },
    );
    if (
      !record ||
      typeof record.fileName !== "string" ||
      !record.bytes ||
      record.scope?.principalId !== normalizedScope.principalId ||
      record.scope?.projectId !== normalizedScope.projectId
    ) {
      return null;
    }
    return record;
  } catch (error) {
    console.warn("[document-source] could not be read back", error);
    return null;
  } finally {
    db?.close();
  }
}

export async function clearDocumentSource(
  scope: DocumentSourceScope,
): Promise<void> {
  const recordKey = documentSourceScopeKey(scope);
  if (!hasIndexedDb() || !recordKey) return;
  let db: IDBDatabase | null = null;
  try {
    db = await openDb();
    const database = db;
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, "readwrite");
      transaction.objectStore(STORE).delete(recordKey);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () =>
        reject(transaction.error ?? new Error("document source clear failed"));
    });
  } catch (error) {
    console.warn("[document-source] could not be cleared", error);
  } finally {
    db?.close();
  }
}

/**
 * Remove one stale publication only when the same source still owns the
 * scoped record. The read and delete share one readwrite transaction, so a
 * newer same-scope write cannot land between comparison and deletion.
 */
export async function clearDocumentSourceIfMatches(
  scope: DocumentSourceScope,
  expected: Pick<StoredDocumentSource, "fileName" | "sha256">,
): Promise<boolean> {
  const normalizedScope = normalizeScope(scope);
  const recordKey = normalizedScope
    ? documentSourceScopeKey(normalizedScope)
    : null;
  if (!hasIndexedDb() || !normalizedScope || !recordKey) return false;
  let db: IDBDatabase | null = null;
  try {
    db = await openDb();
    const database = db;
    return await new Promise<boolean>((resolve, reject) => {
      const transaction = database.transaction(STORE, "readwrite");
      const store = transaction.objectStore(STORE);
      const request = store.get(recordKey);
      let matched = false;
      request.onsuccess = () => {
        const record = request.result as StoredDocumentSource | undefined;
        if (
          record?.scope?.principalId === normalizedScope.principalId &&
          record.scope.projectId === normalizedScope.projectId &&
          record.fileName === expected.fileName &&
          record.sha256 === expected.sha256
        ) {
          matched = true;
          store.delete(recordKey);
        }
      };
      request.onerror = () =>
        reject(request.error ?? new Error("document source compare failed"));
      transaction.oncomplete = () => resolve(matched);
      transaction.onerror = () =>
        reject(transaction.error ?? new Error("document source clear failed"));
      transaction.onabort = () =>
        reject(transaction.error ?? new Error("document source clear aborted"));
    });
  } catch (error) {
    console.warn("[document-source] stale record cleanup failed", error);
    return false;
  } finally {
    db?.close();
  }
}

/**
 * Privacy boundary for logout. This intentionally clears every locally cached
 * document rather than trying to retain another principal's browser data.
 */
export async function clearAllDocumentSourcesForLogout(): Promise<void> {
  if (!hasIndexedDb()) return;
  let db: IDBDatabase | null = null;
  try {
    db = await openDb();
    const database = db;
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, "readwrite");
      transaction.objectStore(STORE).clear();
      transaction.oncomplete = () => resolve();
      transaction.onerror = () =>
        reject(transaction.error ?? new Error("document sources clear failed"));
    });
  } catch (error) {
    console.warn("[document-source] logout cleanup failed", error);
  } finally {
    db?.close();
  }
}
